/**
 * Public /terms page. Content reflects Quelby's actual mechanics (whitelist +
 * mod-invite access model, no payment processing, DMCA/Content-ID exposure
 * from playing requested video audio on stream — see the unmuted-audio
 * warning in StreamView.tsx) rather than generic boilerplate. Not a
 * substitute for legal review; the bracketed governing-law jurisdiction and
 * contact address should be confirmed before this is relied on in production.
 */
import { LegalPage } from "../components/LegalPage";

export function Terms() {
  return (
    <LegalPage title="Terms of Service" updated="July 27, 2026">
      <p>
        These terms govern your use of quelby. By signing in or otherwise using the service, you
        agree to them. If you don't agree, don't use quelby.
      </p>

      <h2>The service</h2>
      <p>
        quelby lets a streamer and their moderators manage a donation and media-request queue
        across Twitch and YouTube, and render an overlay for streaming software (e.g. OBS). Access
        to owning a channel ("streamer" access) is whitelisted at our discretion; a streamer may
        grant moderator access to other accounts by handle.
      </p>

      <h2>Accounts</h2>
      <p>
        You need a Twitch or YouTube account to use quelby — we don't offer a separate login. You're
        responsible for the activity that happens under your account, and a streamer is
        responsible for the moderators they invite and the channel settings they configure,
        including which chat commands and donation-alert integrations are enabled.
      </p>

      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use quelby for any unlawful purpose, or in a way that violates Twitch's or YouTube's own terms of service;</li>
        <li>Attempt to circumvent the whitelist, authentication, or per-channel access controls;</li>
        <li>Interfere with or disrupt the service (e.g. excessive automated requests, attempting to access another channel's data); or</li>
        <li>Submit content, requests, or notes that are unlawful, harassing, or infringe someone else's rights.</li>
      </ul>
      <p>
        We may suspend or terminate access for violating these terms, at our discretion, with or
        without notice.
      </p>

      <h2>Donations aren't processed by quelby</h2>
      <p>
        quelby does not process payments, is not a money transmitter, and never handles payment
        card or bank details. Any donation amount shown in the queue is recorded from a message
        posted by a third-party donation-alert tool (e.g. Streamlabs, StreamElements) that you've
        configured for your own stream — the actual transaction, and any refund or dispute over
        it, is between the donor, the streamer, and that third-party platform. quelby is not
        responsible for the accuracy of donation amounts reported by a third-party bot, or for any
        financial transaction underlying them.
      </p>

      <h2>Video &amp; audio content, and copyright</h2>
      <p>
        Media requests typically reference third-party video content (e.g. YouTube). You're
        responsible for how you use that content on your own stream, including compliance with the
        platform's terms and applicable copyright law. In particular: playing a requested video's
        audio unmuted on a live stream can trigger a Content ID match or a DMCA claim against your
        stream or its recording if you don't hold the rights to that audio — quelby surfaces this
        as a warning in the app, but the choice to unmute is yours, and you assume that risk.
      </p>

      <h2>Your content</h2>
      <p>
        You retain any rights you have in requests, notes, and other content you submit through
        quelby. You grant us the right to store and display that content as needed to operate the
        queue and overlay for your channel. A streamer and their moderators control and moderate
        the content on their own channel's queue.
      </p>

      <h2>Third-party services</h2>
      <p>
        quelby integrates with Twitch and YouTube/Google, whose own terms of service and API
        policies also apply to your use of those platforms through quelby. We aren't responsible
        for the availability, accuracy, or behavior of those third-party platforms.
      </p>

      <h2>Disclaimer of warranties</h2>
      <p>
        quelby is provided "as is," without warranties of any kind, express or implied, including
        merchantability, fitness for a particular purpose, and non-infringement. We don't guarantee
        the service will be uninterrupted, error-free, or that queue ordering or donation matching
        will always be accurate.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, quelby and its operators aren't liable for any
        indirect, incidental, special, or consequential damages arising from your use of the
        service, including lost donations, lost streaming revenue, or platform enforcement action
        (e.g. a Content ID claim or DMCA takedown) resulting from content played on your stream.
      </p>

      <h2>Termination</h2>
      <p>
        You may stop using quelby and request account deletion at any time. We may suspend or
        terminate your access for violating these terms or if required by law.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We may update these terms as the service changes. We'll update the "Last updated" date
        above when we do; continued use after a change means you accept the updated terms.
      </p>

      <h2>Governing law</h2>
      <p>
        These terms are governed by the laws of the Commonwealth of Pennsylvania, USA, without
        regard to its conflict-of-laws principles.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about these terms? Email <a href="mailto:quelby.io@gmail.com">quelby.io@gmail.com</a>.
      </p>
    </LegalPage>
  );
}
